import { Link } from 'react-router-dom'
import {
  ArrowLeft,
  ArrowRight,
  Building2,
  CheckCircle2,
  Truck,
  Users,
  Bell,
  MapPin,
  ShieldCheck,
  Package,
  ClipboardList
} from 'lucide-react'

const roles = [
  {
    id: 'volunteer',
    title: 'Volunteer',
    Icon: Users,
    accent: 'bg-accent-50 text-accent-700 border-accent-100',
    iconWrap: 'bg-accent-100 text-accent-700',
    summary:
      'Volunteers are the bridge between surplus food and people who need it. You claim open donations, pick them up from suppliers, and deliver them to a food bank you choose.',
    steps: [
      {
        title: 'Create your account and finish setup',
        detail:
          'Sign up as a volunteer, verify your email, and complete onboarding with your profile and home location so nearby pickups are easy to find. Upload photo ID for verification — approved volunteers can claim deliveries.'
      },
      {
        title: 'Browse and claim donations',
        detail:
          'Open available deliveries from suppliers. Review title, weight, description, pickup address, and urgency. When you claim a donation, choose which food bank will receive the food.'
      },
      {
        title: 'Get confirmation notifications',
        detail:
          'After you claim a donation, you receive confirmation that the pickup is yours. The donation moves out of the open list so other volunteers know it is taken.'
      },
      {
        title: 'Pick up from the supplier',
        detail:
          'Go to the supplier at the arranged time, collect the food, and keep it safe for transport. The supplier confirms pickup in the app once you have the donation.'
      },
      {
        title: 'Deliver to the food bank',
        detail:
          'Drop the food off at the food bank you selected when claiming. The food bank confirms delivery when they receive it.'
      },
      {
        title: 'Complete the loop and earn recognition',
        detail:
          'Once both the supplier (pickup) and food bank (delivery) have confirmed, the delivery is marked complete. Your volunteer activity is recorded, and you can earn badges and recognition for completed deliveries.'
      }
    ]
  },
  {
    id: 'supplier',
    title: 'Supplier',
    Icon: Truck,
    accent: 'bg-secondary-50 text-secondary-700 border-secondary-100',
    iconWrap: 'bg-secondary-100 text-secondary-700',
    summary:
      'Suppliers (markets, restaurants, bakeries, and other food businesses) post surplus food that would otherwise go to waste so volunteers can rescue it for local food banks.',
    steps: [
      {
        title: 'Register and get approved',
        detail:
          'Sign up as a supplier, verify your email, complete onboarding with your business details and hours, submit business verification, and wait for admin approval before creating donations.'
      },
      {
        title: 'Post a donation',
        detail:
          'From your dashboard, create a donation with a clear title, estimated weight, description (what it is, when it is available, any handling notes), and an optional photo. Your profile address is used as the pickup location.'
      },
      {
        title: 'Wait for a volunteer to claim it',
        detail:
          'Volunteers browse open donations and claim the ones they can handle. When someone claims yours, you can see who is picking it up and which food bank they selected as the destination.'
      },
      {
        title: 'Hand off the food and confirm pickup',
        detail:
          'When the volunteer arrives, give them the donation and confirm pickup in HarvestHelp. That tells the food bank the food is on the way.'
      },
      {
        title: 'Track completion',
        detail:
          'After the food bank confirms delivery, the donation is complete. You can archive older listings and keep posting new surplus as it comes up — reducing waste while helping neighbors.'
      }
    ]
  },
  {
    id: 'food-bank',
    title: 'Food Bank',
    Icon: Building2,
    accent: 'bg-primary-50 text-primary-700 border-primary-100',
    iconWrap: 'bg-primary-100 text-primary-700',
    summary:
      'Food banks and pantries receive rescued donations and can also post current needs so the community knows what would help most.',
    steps: [
      {
        title: 'Register and get approved',
        detail:
          'Sign up as a food bank, verify your email, complete onboarding (organization info, address, hours), submit business verification, and wait for admin approval before creating supply requests.'
      },
      {
        title: 'Post current needs (optional but powerful)',
        detail:
          'Create supply requests describing what you need and when you can receive it. Logged-in users see these on your directory listing so suppliers and volunteers understand your priorities.'
      },
      {
        title: 'Receive incoming deliveries',
        detail:
          'When a volunteer claims a supplier donation and selects your food bank as the destination, that delivery appears in your incoming list so your team knows what to expect.'
      },
      {
        title: 'Confirm delivery when food arrives',
        detail:
          'After the volunteer drops off the food, confirm delivery in the app. Combined with the supplier’s pickup confirmation, this closes the loop and credits the volunteer’s completed delivery.'
      },
      {
        title: 'Keep serving your community',
        detail:
          'Archive old requests, update hours and directory info, and keep accepting rescued food. HarvestHelp is built so pantries get what they need without food going to the landfill.'
      }
    ]
  }
]

const HowItWorksPage = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-primary-50">
      <section className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center max-w-3xl mx-auto">
            <Link
              to="/"
              className="inline-flex items-center text-gray-600 hover:text-primary-600 mb-6"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Home
            </Link>
            <h1 className="text-4xl font-bold text-gray-900 mb-4">How It Works</h1>
            <p className="text-xl text-gray-600">
              HarvestHelp connects three roles so surplus food moves quickly from businesses to
              families — with clear confirmations at every step.
            </p>
          </div>
        </div>
      </section>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12 space-y-10">
        <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-3">The big picture</h2>
          <p className="text-gray-700 leading-relaxed mb-6">
            Suppliers post surplus food. Volunteers claim those donations and choose a food bank.
            Food banks receive the delivery and confirm arrival. Both pickup and drop-off are
            confirmed in the app so everyone stays aligned — and volunteers get credit for completed
            rescues.
          </p>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3 sm:gap-4 text-sm font-semibold">
            <div className="flex items-center justify-center gap-2 rounded-lg bg-secondary-50 text-secondary-800 px-4 py-3 border border-secondary-100">
              <Truck className="h-4 w-4" />
              Supplier posts
            </div>
            <ArrowRight className="hidden sm:block h-5 w-5 text-gray-400 mx-auto" />
            <div className="flex items-center justify-center gap-2 rounded-lg bg-accent-50 text-accent-800 px-4 py-3 border border-accent-100">
              <Users className="h-4 w-4" />
              Volunteer delivers
            </div>
            <ArrowRight className="hidden sm:block h-5 w-5 text-gray-400 mx-auto" />
            <div className="flex items-center justify-center gap-2 rounded-lg bg-primary-50 text-primary-800 px-4 py-3 border border-primary-100">
              <Building2 className="h-4 w-4" />
              Food bank receives
            </div>
          </div>
        </div>

        <div className="flex flex-wrap gap-3 justify-center">
          {roles.map(({ id, title, Icon, accent }) => (
            <a
              key={id}
              href={`#${id}`}
              className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold ${accent}`}
            >
              <Icon className="h-4 w-4" />
              {title}
            </a>
          ))}
        </div>

        {roles.map(({ id, title, Icon, iconWrap, summary, steps }) => (
          <section
            key={id}
            id={id}
            className="bg-white rounded-xl shadow-lg border border-gray-100 p-8 scroll-mt-28"
          >
            <div className="flex items-start gap-4 mb-6">
              <div className={`rounded-2xl p-3 ${iconWrap}`}>
                <Icon className="h-7 w-7" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">{title}</h2>
                <p className="text-gray-600 mt-2 leading-relaxed">{summary}</p>
              </div>
            </div>

            <ol className="space-y-5">
              {steps.map((step, index) => (
                <li key={step.title} className="flex gap-4">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gray-900 text-white text-sm font-bold flex items-center justify-center">
                    {index + 1}
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">{step.title}</h3>
                    <p className="text-gray-600 leading-relaxed text-sm sm:text-base">{step.detail}</p>
                  </div>
                </li>
              ))}
            </ol>
          </section>
        ))}

        <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Confirmations that keep everyone honest</h2>
          <ul className="space-y-3 text-gray-700">
            <li className="flex gap-3">
              <Bell className="h-5 w-5 text-primary-600 shrink-0 mt-0.5" />
              <span>
                Volunteers get notified when a claim succeeds so they know the pickup is reserved for them.
              </span>
            </li>
            <li className="flex gap-3">
              <Package className="h-5 w-5 text-secondary-600 shrink-0 mt-0.5" />
              <span>
                Suppliers confirm pickup after handing off food — proving the donation left the business.
              </span>
            </li>
            <li className="flex gap-3">
              <CheckCircle2 className="h-5 w-5 text-accent-600 shrink-0 mt-0.5" />
              <span>
                Food banks confirm delivery on arrival — completing the rescue and unlocking volunteer credit.
              </span>
            </li>
            <li className="flex gap-3">
              <ShieldCheck className="h-5 w-5 text-gray-700 shrink-0 mt-0.5" />
              <span>
                Food banks and suppliers need email verification, onboarding, business verification, and
                admin approval before posting donations or supply requests.
              </span>
            </li>
            <li className="flex gap-3">
              <MapPin className="h-5 w-5 text-primary-600 shrink-0 mt-0.5" />
              <span>
                Directories, maps, and distance sorting help volunteers and orgs find partners near them
                in the Triangle.
              </span>
            </li>
            <li className="flex gap-3">
              <ClipboardList className="h-5 w-5 text-secondary-600 shrink-0 mt-0.5" />
              <span>
                Food banks can publish current needs; suppliers can attach photos and notes so pickups go
                smoothly.
              </span>
            </li>
          </ul>
        </div>

        <div className="text-center space-y-4">
          <p className="text-gray-600">Ready to join the loop?</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/register" className="btn-primary inline-flex items-center justify-center">
              Get Started
              <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
            <Link to="/our-story" className="btn-outline inline-flex items-center justify-center">
              Read Our Story
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}

export default HowItWorksPage
